import React from "react";
import partnershipMarketPlace from "../assets/images/partnership-microsoft-azure-marketplace.png";
import BulletCardList from "../components/common/BulletCard/BulletCardList";
import temporalPartnerLogo from "../assets/images/temporal-partner-logo.png";
import {
  ComputerDesktopIcon,
  Square3Stack3DIcon,
  ArrowsPointingOutIcon,
  Cog6ToothIcon,
  CloudArrowDownIcon,
} from "@heroicons/react/24/outline";

const Temporal = () => {
  const services = [
    {
      icon: ComputerDesktopIcon,
      label: "Durable Workflow Design",
      description:
        "Build workflows that preserve state, recover from failure, and run reliably end to end.",
    },
    {
      icon: Square3Stack3DIcon,
      label: "Application Modernization",
      description:
        "Replace brittle queues, cron jobs, and retry logic with durable Temporal workflows.",
    },
    {
      icon: ArrowsPointingOutIcon,
      label: "Temporal Platform Engineering",
      description:
        "Stand up Temporal Cloud or self-hosted foundations built for scale and operations.",
    },
    {
      icon: Cog6ToothIcon,
      label: "Reliability & Observability",
      description:
        "Give teams visibility into workflow status, failures, recovery, and next actions.",
    },
  ];

  const azureOfferingValues = [
    {
      icon: CloudArrowDownIcon,
      img: null,
      img_link: null,
      label: "Temporal Readiness & Architecture",
      description:
        "Identify the right use cases and design the foundation before production build begins.",
    },
    {
      icon: Square3Stack3DIcon,
      img: null,
      img_link: null,
      label: "Application Modernization & Migration",
      description:
        "Move existing orchestration onto Temporal one workflow at a time.",
    },
    {
      icon: ComputerDesktopIcon,
      img: partnershipMarketPlace,
      img_link: null,
      label: "Platform Deployment & Operations",
      description:
        "Deploy and operate Temporal consistently across environments.",
    },
    {
      icon: Cog6ToothIcon,
      img: partnershipMarketPlace,
      img_link: null,
      label: "Mission-Critical Workflow Engineering",
      description:
        "Build production-ready workflows for critical business processes.",
    },
  ];

  return (
    <div className="container mx-auto pt-8 px-6 sm:px-12 md:px-24 lg:px-28">
      <div className="my-6">
        <h1 className="text-4xl font-bold mb-10 text-gray-900 sm:text-5xl">
          Our <span className="text-purple-700">Temporal</span> Partnership and
          Services
        </h1>
        <div className="flex flex-row flex-wrap lg:flex-nowrap justify-center gap-4">
          <div className="bg-white rounded-lg shadow-md p-8">
            <img
              src={temporalPartnerLogo}
              alt="Temporal Partner Logo"
              className="w-full max-w-xs lg:max-w-sm h-auto"
            />
          </div>
        </div>
        <p className="mt-14">
          RAVL helps highly regulated organizations design, build, and scale
          reliable applications on Temporal. We combine strategic advisory,
          hands-on engineering, and sustainable talent development to turn
          fragile, hard-to-recover processes into durable workflows that run to
          completion, every time.
        </p>
      </div>

      <div className="my-14">
        <h3 className="text-lg sm:text-xl font-semibold text-purple-700">
          Our Temporal Services
        </h3>
        <p className="mt-2">
          At RAVL, we offer a complete range of Temporal consulting services to
          help you unlock the full potential of Temporal and achieve your
          business goals.
        </p>
      </div>
      <div className="my-14">
        <BulletCardList values={services} />
      </div>

      <div className="my-14">
        <h3 className="text-lg sm:text-xl font-semibold text-purple-700">
          Our Temporal Offerings
        </h3>
        <div className="mt-2">
          {azureOfferingValues.map((offering, index) => (
            <div
              className={`card text-left max-w-3xl self-center my-8 ${index % 2 ? "ml-auto" : "mr-auto"}`}
              key={index}
            >
              <div className="flex flex-col md:flex-row items-start gap-4">
                <div className="flex-grow flex flex-col justify-center">
                  <div className="flex items-center gap-4">
                    {offering.icon && (
                      <offering.icon
                        aria-hidden="true"
                        focusable="false"
                        className="w-6 h-6 stroke-purple-500 fill-transparent"
                      />
                    )}
                    <h4 className="font-semibold text-purple-700">
                      {offering.label}
                    </h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    {offering.description}
                  </p>
                </div>
                {offering.img && offering.img_link && (
                  <a
                    href={offering.img_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-0 md:w-auto w-full"
                  >
                    <img
                      src={offering.img}
                      alt="Azure Offering"
                      className="w-3/4 md:w-1/4 lg:w-full h-auto mx-auto md:mx-0"
                    />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="my-14">
        <h3 className="text-lg sm:text-xl font-semibold text-purple-700">
          Why Choose RAVL?
        </h3>
        <p className="mt-2">
          RAVL brings deep engineering experience, financial services roots, and
          practical execution discipline to Temporal initiatives. We help
          organizations move from strategy to production with reliable
          workflows, repeatable patterns, and teams that can sustain the work
          long after delivery. Our approach is deliberately practical: start
          with the business process, identify where failure and operational
          complexity create risk, then design the workflow, platform, and
          delivery model around the outcome the organization needs. That means
          helping teams avoid over-engineering, build the right first use case,
          and create patterns that can scale beyond a single implementation.
        </p>
      </div>
      <div className="my-14">
        {/* <h3 className="text-lg sm:text-xl font-semibold text-purple-700 mb-8">
          The Work We've Done:
        </h3>
        <Carousel caseStudies={selectedCaseStudies} /> */}
        <div className="my-14">
          <h3 className="text-lg sm:text-xl font-semibold text-purple-700 mt-10">
            Let's Talk!
          </h3>
          <p className="mt-2">
            Ready to build applications you can count on? Contact RAVL to
            explore how durable workflows on Temporal can make your critical
            processes reliable, observable, and built to scale.
          </p>
          <p className="mt-2">
            Whether you are evaluating Temporal for the first time, modernizing
            brittle orchestration, or looking for a safer way to run
            mission-critical workflows, RAVL can help you identify the right
            starting point and build a practical path forward.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Temporal;
